import { useEffect } from "react";

/**
 * Tell the OS "this tab is an active voice call" via the Media Session
 * API. This matters because mobile browsers aggressively throttle (and
 * eventually suspend) backgrounded tabs — including WebRTC audio — to
 * save battery. The only reliable escape hatch is MediaSession:
 *
 *  • iOS Safari (≥16.4): a tab with `playbackState: 'playing'` and
 *    MediaSession metadata is treated as media playback. The audio
 *    track continues playing after the user switches apps, and the
 *    call appears in Control Center / Lock Screen with a stop button.
 *
 *  • Chrome Android: same behavior, plus the OS shows a persistent
 *    media notification that the user can tap to come back.
 *
 *  • Bethanie APK (WebView wrapper): inherits whatever MediaSession
 *    policy the host app enables. If the host enables background
 *    audio, this hook lights it up with a hangup action.
 *
 * We register a `stop` / `hangup` action so the OS media controls can
 * end the call directly from the lock screen without unlocking the
 * phone.
 *
 * `onHangup` is wrapped in a ref inside the caller so the handler is
 * always up-to-date even though we only register once per activation.
 */
export default function useCallMediaSession({ active, title, onHangup }) {
  useEffect(() => {
    if (!active) return undefined;
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
      return undefined;
    }
    const ms = navigator.mediaSession;
    try {
      ms.metadata = new window.MediaMetadata({
        title: title || "Voice call",
        artist: "Church Hub",
      });
    } catch { /* older Safari lacks MediaMetadata — safe to skip */ }

    try { ms.playbackState = "playing"; } catch { /* ignore */ }

    const safeHangup = () => { try { onHangup?.(); } catch { /* ignore */ } };
    const actions = ["stop", "pause"];
    for (const a of actions) {
      try { ms.setActionHandler(a, safeHangup); } catch { /* unsupported action, skip */ }
    }

    return () => {
      try { ms.playbackState = "none"; } catch { /* ignore */ }
      for (const a of actions) {
        try { ms.setActionHandler(a, null); } catch { /* ignore */ }
      }
      try { ms.metadata = null; } catch { /* ignore */ }
    };
  }, [active, title, onHangup]);
}
